import { useState, useEffect, useRef } from 'react';

interface BenefitsSectionProps {
  onDownloadClick: () => void;
}

const benefits = [
  {
    icon: 'ri-search-eye-line',
    title: 'Virtual Roof Estimate',
    description: 'No in-person inspection required. We send you a comprehensive quote tailored to your home within 24 hours using satellite technology.'
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'Lowest Price Quote',
    description: 'We guarantee to be below your estimated price or our service is FREE. Transparent flat fee structure with no hidden margins or surprise pricing.'
  },
  {
    icon: 'ri-tools-line',
    title: 'Premium Materials at Fraction of Cost',
    description: 'Experience premium roof replacement with the best materials and craftsmanship — all at a fraction of the cost of traditional companies.'
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Lifetime Warranty & Quality',
    description: 'Top-tier materials from brands like CertainTeed, Tamko, IKO, and Owens Corning. Fully licensed and insured professionals committed to excellence.'
  },
  {
    icon: 'ri-timer-flash-line',
    title: 'Installed in Days',
    description: 'Fast scheduling and professional installation. Most projects completed in just days, not weeks. Get your new roof faster than ever.'
  },
  {
    icon: 'ri-hand-coin-line',
    title: 'Millions Saved',
    description: 'We\'ve saved homeowners millions of dollars from their lowest roofing estimates every year since 2021. Join thousands of satisfied customers.'
  }
];

const BenefitsSection = ({ onDownloadClick }: BenefitsSectionProps) => {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const [ctaVisible, setCtaVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === sectionRef.current) {
              benefits.forEach((_, index) => {
                setTimeout(() => {
                  setVisibleCards((prev) => [...prev, index]);
                }, index * 100);
              });
            }
            if (entry.target === ctaRef.current) {
              setCtaVisible(true);
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            Roof Replacement That Makes Sense
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Transparent pricing. Premium materials. Exceptional workmanship. The straightforward way to get a quality roof at the lowest price.
          </p>
        </div>

        <div ref={sectionRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`p-6 md:p-8 bg-gray-50 rounded-xl hover:shadow-xl transition-all duration-500 hover:-translate-y-2 cursor-pointer group ${
                visibleCards.includes(index) 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-slate-100 rounded-lg mb-4 md:mb-6 group-hover:bg-[#4A90E2] transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
                <i className={`${benefit.icon} text-2xl md:text-3xl text-slate-700 group-hover:text-white transition-colors duration-300`}></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-[#2B5BA6] transition-colors duration-300">
                {benefit.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Customer Satisfaction Guarantee */}
        <div className="bg-gradient-to-br from-[#4A90E2]/10 to-[#2B5BA6]/10 rounded-2xl p-6 sm:p-8 md:p-10 mb-12 md:mb-16">
          <div className="text-center max-w-4xl mx-auto">
            <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center bg-[#4A90E2] rounded-full mx-auto mb-4 md:mb-6">
              <i className="ri-medal-line text-3xl md:text-4xl text-white"></i>
            </div>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              Our Unwavering Commitment
            </h3>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
              We aim to offer the most straightforward, transparent cost structure possible. You only pay for the roofing materials, while our pre-negotiated labor rates keep costs low. We make our money solely through a pre-agreed flat fee — <strong>no hidden margins, no surprise pricing</strong> — so you can feel confident that you're getting the best value for your investment.
            </p>
            <p className="text-lg sm:text-xl font-bold text-[#2B5BA6]">
              We guarantee to be below your estimated price or our service is FREE!*
            </p>
          </div>
        </div>

        <div 
          ref={ctaRef}
          className={`bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 sm:p-8 md:p-12 text-center transition-all duration-1000 ${
            ctaVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
            Get Your Free Homeowner's Roofing Guide
          </h3>
          <p className="text-base sm:text-lg text-gray-600 mb-6 md:mb-8 max-w-2xl mx-auto px-4">
            Learn how to protect yourself from overpriced quotes and find quality contractors at fair prices
          </p>
          <button
            onClick={onDownloadClick}
            className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-[#2B5BA6] text-white text-base sm:text-lg font-semibold rounded-lg hover:bg-[#234a8a] transition-all duration-300 shadow-lg hover:shadow-2xl whitespace-nowrap min-h-[48px] transform hover:scale-105 active:scale-95 group"
          >
            <i className="ri-download-line mr-2 text-xl group-hover:animate-bounce"></i>
            Download Free Guide
          </button>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
